import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ExternalLink, Navigation, MapPin, Loader2 } from 'lucide-react';
import { loadGoogleMaps, geocodeAddress, mapsSearchUrl, mapsDirectionsUrl } from '@/lib/googleMaps';

export const PropertyMap = ({ property }) => {
  const mapEl = useRef(null);
  const [pos, setPos] = useState(null);
  const [approx, setApprox] = useState(Boolean(property.location_approx));
  const [state, setState] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    const onAuthFail = () => setState('error');
    window.addEventListener('gmaps-auth-failure', onAuthFail);
    const run = async () => {
      let p = Number.isFinite(property.latitude) && Number.isFinite(property.longitude)
        ? { lat: property.latitude, lng: property.longitude } : null;
      let isApprox = Boolean(property.location_approx) || !p;
      if (isApprox) {
        try {
          const r = await geocodeAddress(`${property.address}, ${property.city}, ${property.state} ${property.pincode}, India`);
          if (r) p = { lat: r.geometry.location.lat(), lng: r.geometry.location.lng() };
        } catch (_) { /* fall back to given coords */ }
      }
      if (cancelled) return;
      if (!p) return setState('error');
      const maps = await loadGoogleMaps();
      if (cancelled || !mapEl.current) return;
      const map = new maps.Map(mapEl.current, {
        center: p, zoom: isApprox ? 13 : 16, mapTypeControl: false, streetViewControl: false, fullscreenControl: true, clickableIcons: false,
        gestureHandling: 'greedy', zoomControl: true,
      });
      if (isApprox) {
        new maps.Circle({ map, center: p, radius: 900, strokeColor: '#D4AF37', strokeOpacity: 0.9, strokeWeight: 2, fillColor: '#D4AF37', fillOpacity: 0.18 });
      } else {
        new maps.Marker({ map, position: p, title: property.title });
      }
      setPos(p); setApprox(isApprox); setState('ready');
    };
    run().catch(() => !cancelled && setState('error'));
    return () => { cancelled = true; window.removeEventListener('gmaps-auth-failure', onAuthFail); };
  }, [property]);

  return (
    <div className="bg-white p-6 rounded-sm shadow-sm border border-border/50" data-testid="property-map-section">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="font-['Plus_Jakarta_Sans'] text-xl font-semibold text-[#0F172A]">Location</h2>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
            <MapPin className="h-4 w-4" /> {property.address}, {property.city}, {property.state} - {property.pincode}
          </p>
        </div>
        {pos && (
          <div className="flex gap-2">
            <a href={mapsSearchUrl(pos.lat, pos.lng)} target="_blank" rel="noopener noreferrer" data-testid="view-on-google-maps">
              <Button type="button" variant="outline" size="sm"><ExternalLink className="h-4 w-4 mr-2" /> View on Google Maps</Button>
            </a>
            <a href={mapsDirectionsUrl(pos.lat, pos.lng)} target="_blank" rel="noopener noreferrer" data-testid="get-directions">
              <Button type="button" size="sm" className="bg-[#0F172A] text-white hover:bg-[#1E293B]"><Navigation className="h-4 w-4 mr-2" /> Get Directions</Button>
            </a>
          </div>
        )}
      </div>
      <div className="relative rounded-md overflow-hidden border border-border/60">
        <div ref={mapEl} className="h-[320px] w-full bg-[#E5E7EB]" data-testid="property-map" />
        {state === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading map…
          </div>
        )}
        {state === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/90 text-sm text-muted-foreground p-6 text-center" data-testid="property-map-error">
            Map unavailable for this listing right now.
          </div>
        )}
        {state === 'ready' && approx && (
          <span className="absolute left-3 top-3 rounded-full bg-white/95 px-3 py-1 text-xs font-medium text-[#0F172A] shadow" data-testid="approx-location-badge">
            Approximate location — exact pin shared on enquiry
          </span>
        )}
      </div>
    </div>
  );
};
